import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { CheckBadgeIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { useOAuthConsentInfo, useSubmitOAuthConsent } from '@/api/hooks/useOAuthConsent';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { LogoFull } from '@/components/LogoFull';
import { usePageTitle } from '@/hooks/usePageTitle';
import { describeOAuthScope } from '@/utils/oauthScopes';
import { sanitizeAuthorizeReturnTo } from '@/utils/returnTo';
import { getErrorMessage } from '@/utils/errorHandler';
import type { OAuthConsentDecision } from '@/api/types';

interface RequestedScope {
    id: string;
    description: string;
    isAlreadyGranted: boolean;
}

interface ConsentShellProps {
    children: ReactNode;
}

function ConsentShell({ children }: ConsentShellProps) {
    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4 dark:bg-dark sm:p-8">
            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <Link to="/">
                        <LogoFull className="mx-auto h-10 w-auto" />
                    </Link>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-xs dark:border-gray-700 dark:bg-secondary sm:p-8">
                    {children}
                </div>
            </div>
        </div>
    );
}

export function OAuthConsentPage() {
    usePageTitle('Authorize app');

    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

    const clientId = searchParams.get('client_id') ?? '';
    const scopeParam = searchParams.get('scope') ?? '';
    const returnTo = sanitizeAuthorizeReturnTo(searchParams.get('returnTo'));

    const [decisionError, setDecisionError] = useState<string | null>(null);
    const [pendingDecision, setPendingDecision] = useState<OAuthConsentDecision | null>(null);

    // The request is held back until the link is trustworthy and the user is signed in.
    const canLoad = isAuthenticated && returnTo !== null && clientId !== '';
    const { data, isLoading, error } = useOAuthConsentInfo(canLoad ? clientId : null);
    const submitConsent = useSubmitOAuthConsent();

    const requestedScopes = useMemo<RequestedScope[]>(() => {
        if (!data) {
            return [];
        }

        const granted = new Set(data.alreadyGranted);
        const known = new Map(data.scopes.map((scope) => [scope.id as string, scope.description]));
        const requestedIds = scopeParam.split(' ').filter((id) => id.length > 0);
        const ids = requestedIds.length > 0 ? requestedIds : data.scopes.map((scope) => scope.id);

        return ids.map((id) => ({
            id,
            description: known.get(id) ?? describeOAuthScope(id),
            isAlreadyGranted: granted.has(id),
        }));
    }, [data, scopeParam]);

    async function handleDecision(decision: OAuthConsentDecision) {
        if (returnTo === null) {
            return;
        }

        setDecisionError(null);
        setPendingDecision(decision);

        try {
            await submitConsent.mutateAsync({
                clientId,
                scopes: requestedScopes.map((scope) => scope.id),
                decision,
            });

            // A full navigation is required: the API answers with a redirect back to the client.
            window.location.assign(decision === 'approve' ? returnTo : `${returnTo}&denied=1`);
        } catch (err) {
            setDecisionError(getErrorMessage(err));
            setPendingDecision(null);
        }
    }

    if (isAuthLoading) {
        return (
            <ConsentShell>
                <p className="text-center text-gray-500 dark:text-gray-400">Loading...</p>
            </ConsentShell>
        );
    }

    if (!isAuthenticated) {
        const target = `${location.pathname}${location.search}`;
        return <Navigate to={`/login?returnTo=${encodeURIComponent(target)}`} replace />;
    }

    if (returnTo === null || clientId === '') {
        return (
            <ConsentShell>
                <h1 className="mb-4 text-center text-xl font-bold text-gray-900 dark:text-white">
                    This authorization link is not valid
                </h1>
                <ErrorAlert message="The link is incomplete or points somewhere else. Start the connection again from the app you want to authorize." />
                <div className="mt-6 text-center">
                    <Link
                        to="/dashboard"
                        className="text-sm font-medium text-gray-600 underline hover:no-underline dark:text-gray-300"
                    >
                        Go to your dashboard
                    </Link>
                </div>
            </ConsentShell>
        );
    }

    if (isLoading) {
        return (
            <ConsentShell>
                <p className="text-center text-gray-500 dark:text-gray-400">
                    Loading app details...
                </p>
            </ConsentShell>
        );
    }

    if (error || !data) {
        return (
            <ConsentShell>
                <h1 className="mb-4 text-center text-xl font-bold text-gray-900 dark:text-white">
                    We could not load this app
                </h1>
                <ErrorAlert message={error ? getErrorMessage(error) : 'Unknown application.'} />
            </ConsentShell>
        );
    }

    const isSubmitting = submitConsent.isPending;

    return (
        <ConsentShell>
            <div className="text-center">
                <ShieldCheckIcon
                    className="mx-auto h-10 w-10 text-accent"
                    aria-hidden="true"
                />
                <h1 className="mt-3 text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">
                    {data.client.name}
                </h1>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    wants to access your Netfiscus account
                </p>
            </div>

            <h2 className="mt-6 text-sm font-medium text-gray-900 dark:text-white">
                It is asking to
            </h2>
            <ul className="mt-2 space-y-2">
                {requestedScopes.map((scope) => (
                    <li
                        key={scope.id}
                        data-testid={`scope-${scope.id}`}
                        className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-dark"
                    >
                        <p className="text-sm text-gray-900 dark:text-gray-100">
                            {scope.description}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                            <code className="font-mono text-xs text-gray-500 dark:text-gray-400">
                                {scope.id}
                            </code>
                            {scope.isAlreadyGranted && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                    <CheckBadgeIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                    Already granted
                                </span>
                            )}
                        </div>
                    </li>
                ))}
            </ul>

            {data.client.redirectHosts.length > 0 && (
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                    After you approve, you are sent back to{' '}
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                        {data.client.redirectHosts.join(', ')}
                    </span>
                    .
                </p>
            )}

            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                You can revoke this access at any time in Settings, under Connected apps.
            </p>

            {decisionError && (
                <div className="mt-4">
                    <ErrorAlert message={decisionError} />
                </div>
            )}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse">
                <Button
                    type="button"
                    className="w-full"
                    onClick={() => handleDecision('approve')}
                    disabled={isSubmitting}
                    isLoading={isSubmitting && pendingDecision === 'approve'}
                >
                    Approve
                </Button>
                <Button
                    type="button"
                    variant="secondary"
                    className="w-full"
                    onClick={() => handleDecision('deny')}
                    disabled={isSubmitting}
                    isLoading={isSubmitting && pendingDecision === 'deny'}
                >
                    Deny
                </Button>
            </div>
        </ConsentShell>
    );
}
